<script setup lang="ts">
import { ref, reactive } from "vue";
import {
  Time,
  parseTime,
  increaseTime,
  decreaseTime,
  copyTime,
  now
} from "@zyzgroup/core-common";
import {
  setTimeoutAccurate,
  setIntervalAccurate,
  injectStyle
} from "@zyzgroup/core-web";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    startTime?: string;
    initStart?: boolean;
    creaseType?: "increase" | "decrease";
    flipDirection?: "down" | "up";
    dur?: number; // 动画时间
    bgColor?: string;
    textColor?: string;
    borderRadius?: number;
    gap?: number;
    perspective?: number;
  }>(),
  {
    startTime: "00:00:00",
    initStart: true,
    creaseType: "increase",
    flipDirection: "down",
    dur: 800, // 动画时间
    bgColor: "#5a82f0",
    textColor: "#ffffff",
    borderRadius: 8,
    gap: 1,
    perspective: 400
  }
);

// const emits = defineEmits<{
//   (event: "click"): void;
// }>();

const containerRef = ref();
const flipWidth = ref(40);
const lineHeight = ref(20);
const fontSize = ref(20);

const resize = (box) => {
  flipWidth.value = Math.floor(box.rect.width / (6 + 1));
  lineHeight.value = Math.floor(box.rect.height);
  fontSize.value = Math.max(12, lineHeight.value);
};

const mount = (box) => {
  // for ce
  injectStyle(
    `
  :root {
    --yz-clock-flipper-bg-color:${props.bgColor};
    --yz-clock-flipper-text-color:${props.textColor};
    --yz-clock-flipper-border-radius:${props.borderRadius}px;
    --yz-clock-flipper-gap:${props.gap}px;
    --yz-clock-flipper-gap-double:${props.gap * 2}px;
    --yz-clock-flipper-gap-four:${props.gap * 4}px;
    --yz-clock-flipper-animation-duration:${props.dur}ms;
    --yz-clock-flipper-perspective: ${props.perspective}px;
  }
  :host {
    --yz-clock-flipper-bg-color:${props.bgColor};
    --yz-clock-flipper-text-color:${props.textColor};
    --yz-clock-flipper-border-radius:${props.borderRadius}px;
    --yz-clock-flipper-gap:${props.gap}px;
    --yz-clock-flipper-gap-double:${props.gap * 2}px;
    --yz-clock-flipper-gap-four:${props.gap * 4}px;
    --yz-clock-flipper-animation-duration:${props.dur}ms;
    --yz-clock-flipper-perspective: ${props.perspective}px;
  }
  `,
    box.el
  );
  if (props.initStart) {
    start(props.startTime);
  }
};

const startTime: Time = [0, 0, 0, 0, 0, 0];
const time = reactive<Time>([0, 0, 0, 0, 0, 0]);
const timeNext = reactive<Time>([0, 0, 0, 0, 0, 0]);

let lastTimeOutCount = 0;

const start = (starttime?: string) => {
  if (starttime) {
    copyTime(parseTime(starttime), startTime);
    startTime.forEach((t, i) => {
      time[i] = t;
      timeNext[i] = t;
    });
  }
  pause();
  lastTimeOutCount = 0;
  flipAction(now(), 0, false);
  clearInterval = setIntervalAccurate(
    (t: DOMHighResTimeStamp, count: number, isTimeOut: boolean) => {
      flipAction(t, count, isTimeOut);
    },
    1000
  );
};

let clearInterval: () => void;
const pause = () => {
  clearInterval?.();
};

const flipAction = (
  _: DOMHighResTimeStamp,
  count: number,
  isTimeOut: boolean
) => {
  // 当前周期为超时周期
  if (isTimeOut) {
    lastTimeOutCount = count;
    // 超时周期不更新
  } else {
    // 当前周期是正常周期，上个周期为超时周期
    if (lastTimeOutCount > 0) {
      // 尽量保持与开始时间的偏差
      startTime.forEach((t, i) => {
        time[i] = t;
        timeNext[i] = t;
      });
      const currentCount = lastTimeOutCount + 1;
      if (props.creaseType == "increase") {
        copyTime(increaseTime(time, currentCount), time);
        copyTime(increaseTime(timeNext, currentCount + 1), timeNext);
      } else {
        copyTime(decreaseTime(time, currentCount), time);
        copyTime(decreaseTime(timeNext, currentCount + 1), timeNext);
      }
      lastTimeOutCount = 0;
    } else {
      // 正常周期
      if (props.creaseType == "increase") {
        copyTime(increaseTime(timeNext, 1), timeNext);
      } else {
        copyTime(decreaseTime(timeNext, 1), timeNext);
      }
    }
    setTimeoutAccurate(() => {
      timeNext.forEach((t, i) => {
        time[i] = t;
      });
    }, props.dur);
  }
};

defineExpose({ start, pause });
</script>

<template>
  <Box
    ref="containerRef"
    display-type="full"
    :style="{ overflow: 'visible' }"
    @mount="mount"
    @resize="resize"
  >
    <div
      class="clock-flipper"
      :style="{
        fontSize: fontSize + 'px',
        lineHeight: lineHeight + 'px'
      }"
    >
      <template
        v-for="(t, i) in time"
        :key="i"
      >
        <!-- 小时 十位 -->
        <!-- 小时 个位 -->
        <!-- gap -->
        <!-- 分钟 十位 -->
        <!-- 分钟 个位 -->
        <!-- gap -->
        <!-- 秒 十位 -->
        <!-- 秒 个位 -->
        <div
          class="flip"
          :class="[props.flipDirection, timeNext[i] != t ? 'go' : '']"
          :style="{ width: flipWidth + 'px' }"
        >
          <div
            class="front digital"
            :class="[`flipper_${i}`, `number_${t}`]"
          ></div>
          <div
            class="back digital"
            :class="[`number_${timeNext[i]}`]"
          ></div>
        </div>
        <div
          v-if="i == 1 || i == 3"
          class="gap"
          :style="{
            fontSize: Math.floor(fontSize * 0.6) + 'px',
            lineHeight: 0
          }"
        >
          :
        </div>
      </template>
    </div>
  </Box>
</template>

<style>
@keyframes frontFlipDown {
  0% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(0deg);
  }
  100% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(-180deg);
  }
}
@keyframes backFlipDown {
  0% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(180deg);
  }
  100% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(0deg);
  }
}
@keyframes frontFlipUp {
  0% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(0deg);
  }
  100% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(180deg);
  }
}
@keyframes backFlipUp {
  0% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(-180deg);
  }
  100% {
    transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(0deg);
  }
}
</style>

<style scoped>
.clock-flipper {
  position: relative;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
  font-family: D-DINCondensed-number;
}
.flip {
  position: relative;
  height: 100%;
  box-sizing: border-box;
  color: var(--yz-clock-flipper-text-color);
  /* border-radius: var(--yz-clock-flipper-border-radius) 0 0
    var(--yz-clock-flipper-border-radius); */
}
.flip + .flip {
  margin-left: var(--yz-clock-flipper-gap-double);
  /* border-radius: 0 var(--yz-clock-flipper-border-radius)
    var(--yz-clock-flipper-border-radius) 0; */
}

/*
-------------- --------------
.back:before  |  .front:before
.back:after   |  .front:after
-------------- ---------------
*/

/* 向下翻
                      .back:after{z-index:2}
                              *    .front:before{z-index:3}
                              *         *
                              *     *
                              *  *
.back:before{z-index:1} * * * *  *  *  *  *  * .front:after{z-index:1}
*/
.flip.down .front:after,
.flip.down .back:before {
  z-index: 1;
}
.flip.down .front:before {
  z-index: 3;
}
.flip.down .back:after {
  z-index: 2;
  transform-origin: 50% 0%;
  transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(180deg);
}
.flip.down.go .front:before {
  transform-origin: 50% 100%;
  animation: frontFlipDown var(--yz-clock-flipper-animation-duration)
    ease-in-out both;
  backface-visibility: hidden;
  box-shadow: 0 -2px 6px rgba(255, 255, 255, 0.3);
}
.flip.down.go .back:after {
  animation: backFlipDown var(--yz-clock-flipper-animation-duration) ease-in-out
    both;
}

/* 向上翻
                      .front:after{z-index:3}
                              *    .back:before{z-index:2}
                              *         *
                              *     *
                              *  *
.front:before{z-index:1} * * * *  *  *  *  *  * .back:after{z-index:1}
*/
.flip.up .front:before,
.flip.up .back:after {
  z-index: 1;
}
.flip.up .front:after {
  z-index: 3;
}
.flip.up .back:before {
  z-index: 2;
  transform-origin: 50% 100%;
  transform: perspective(var(--yz-clock-flipper-perspective)) rotateX(-180deg);
}
.flip.up.go .front:after {
  transform-origin: 50% 0;
  animation: frontFlipUp var(--yz-clock-flipper-animation-duration) ease-in-out
    both;
  backface-visibility: hidden;
  box-shadow: 0 -2px 6px rgba(255, 255, 255, 0.3);
}
.flip.up.go .back:before {
  animation: backFlipUp var(--yz-clock-flipper-animation-duration) ease-in-out
    both;
}

.gap {
  margin: 0 var(--yz-clock-flipper-gap-four);
  color: var(--yz-clock-flipper-bg-color);
}

.digital:before,
.digital:after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 50%;
  box-sizing: border-box;
  background: var(--yz-clock-flipper-bg-color);
  overflow: hidden;
}
.digital:before {
  top: 0;
  bottom: 50%;
  border-radius: var(--yz-clock-flipper-border-radius)
    var(--yz-clock-flipper-border-radius) 0 0;
  border-bottom: solid var(--yz-clock-flipper-gap)
    var(--yz-clock-flipper-text-color);
}
.digital:after {
  top: 50%;
  bottom: 0;
  border-radius: 0 0 var(--yz-clock-flipper-border-radius)
    var(--yz-clock-flipper-border-radius);
  border-top: solid var(--yz-clock-flipper-gap)
    var(--yz-clock-flipper-text-color);
  /* 下半张”纸牌只显示文字的下半部分，并且与“上半张”纸牌衔接在一起 */
  line-height: 0;
}

.digital.number_0:before,
.digital.number_0:after {
  content: "0";
}
.digital.number_1:before,
.digital.number_1:after {
  content: "1";
}
.digital.number_2:before,
.digital.number_2:after {
  content: "2";
}
.digital.number_3:before,
.digital.number_3:after {
  content: "3";
}
.digital.number_4:before,
.digital.number_4:after {
  content: "4";
}
.digital.number_5:before,
.digital.number_5:after {
  content: "5";
}
.digital.number_6:before,
.digital.number_6:after {
  content: "6";
}
.digital.number_7:before,
.digital.number_7:after {
  content: "7";
}
.digital.number_8:before,
.digital.number_8:after {
  content: "8";
}
.digital.number_9:before,
.digital.number_9:after {
  content: "9";
}
</style>
